import axios from 'axios'
import { Auth } from 'aws-amplify'

const client = axios.create({ baseURL: process.env.VUE_APP_BILLING_API || process.env.VUE_APP_ROOT_API, timeout: 30000 })
const storageKey = 'self-biography-referral'
let enrollment
let enrolledSubject
let feature
let featureAt = 0
let memoryVisit
let sessionGeneration = 0

export async function referralsEnabled() {
  if (Date.now() - featureAt < 30000) return feature
  const { data } = await client.get('/billing/catalog')
  feature = data.referralsEnabled === true
  featureAt = Date.now()

  return feature
}

function savedVisit() {
  if (memoryVisit && new Date(memoryVisit.expiresAt).getTime() > Date.now()) return memoryVisit
  try {
    const stored = JSON.parse(localStorage.getItem(storageKey))
    if (stored && stored.token && new Date(stored.expiresAt).getTime() > Date.now()) return stored
    localStorage.removeItem(storageKey)
  } catch (_) { /* Private browsing can disable storage. */ }

  return null
}

export async function captureReferral(referralCode) {
  if (typeof referralCode !== 'string' || !/^[ra]_[a-f0-9]{32}$/.test(referralCode) || savedVisit()) return
  if (!await referralsEnabled()) return
  const { data } = await client.post('/referrals/visit', { code: referralCode })
  memoryVisit = data
  try { localStorage.setItem(storageKey, JSON.stringify(data)) } catch (_) { /* The application remains usable without storage. */ }
}

async function request(method, path, data, key, params) {
  const session = await Auth.currentSession()
  const response = await client.request({
    method,
    url: path,
    data,
    params,
    headers: { Authorization: session.getAccessToken().getJwtToken(), ...(key ? { 'Idempotency-Key': key } : {}) },
  })

  return response.data
}

export async function ensureReferralEnrollment() {
  if (!await referralsEnabled()) return false
  const user = await Auth.currentAuthenticatedUser()
  if (!user.attributes || !user.attributes.email_verified) return false
  const subject = user.attributes.sub
  if (enrolledSubject === subject) return true
  if (enrollment && enrollment.subject === subject) return enrollment.promise
  const visit = savedVisit()
  const generation = sessionGeneration
  const promise = request('post', '/referrals/enroll', visit ? { token: visit.token } : {}).then(() => {
    if (generation !== sessionGeneration) return false
    enrolledSubject = subject
    memoryVisit = null
    try { localStorage.removeItem(storageKey) } catch (_) { /* Storage is optional. */ }

    return true
  }).finally(() => { if (enrollment && enrollment.subject === subject) enrollment = null })
  enrollment = { subject, promise }

  return promise
}

export function resetReferralSession() {
  sessionGeneration += 1
  enrolledSubject = null
  enrollment = null
}

export function canAdminReferrals(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))

    return Array.isArray(payload['cognito:groups']) && payload['cognito:groups'].includes('affiliate-admins')
  } catch (_) { return false }
}

export default {
  async applicationPolicy() {
    const { data } = await client.get('/referrals/policy')

    return data
  },
  async me() {
    if (!await ensureReferralEnrollment()) throw new Error('Referrals are not available yet.')

    return request('get', '/referrals/me')
  },
  apply: application => request('post', '/referrals/apply', { application }),
  async notifications(limit = 20) {
    if (!await referralsEnabled()) return { notifications: [], unreadCount: 0 }

    return request('get', '/referrals/notifications', undefined, undefined, { limit })
  },
  markNotificationsRead: notificationId => request('post', '/referrals/notifications/read', notificationId ? { notificationId } : {}),
  admin: (section, params) => request('get', `/admin/referrals/${section}`, undefined, undefined, params),
  updateAffiliate: (id, status, reason) => request('patch', `/admin/referrals/affiliates/${id}`, { status, reason }),
  updateSettings: settings => request('patch', '/admin/referrals/settings', settings),
  preparePayout: (affiliateId, key) => request('post', '/admin/referrals/payouts', { affiliateId }, key),
  finishPayout: (id, status, reference) => request('patch', `/admin/referrals/payouts/${id}`, { status, reference }),
}
